// SuperAdminDashboard.js
import React, { useState, useEffect } from 'react';
import apiService, { errorHelpers, formatHelpers } from '../../utils/apiService';
import AdminOverview from './admin/AdminOverview';
import UserManagement from './admin/UserManagement';
import CertificateUpload from './admin/CertificateUpload';
import CertificateManagement from './admin/CertificateManagement';
import VerificationReports from './admin/VerificationReports';
import SystemSettings from './admin/SystemSettings';
import ActivityLogs from './admin/ActivityLogs';
import UploadReports from './admin/UploadReports';
import SuperAdminSettings from './superadmin/SuperAdminSettings';
import SystemAnalytics from './superadmin/SystemAnalytics';
import AdminManagement from './superadmin/AdminManagement';

const SuperAdminDashboard = ({ activeTab, setActiveTab, user }) => {
    const [analytics, setAnalytics] = useState(null);
    const [systemHealth, setSystemHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            const [analyticsResponse] = await Promise.all([
                apiService.admin.getDashboardAnalytics(),
                // Add more system health checks here
            ]);

            setAnalytics(analyticsResponse.data.analytics);

            // Simulate system health data
            setSystemHealth({
                database: 'healthy',
                email: 'healthy',
                storage: 'healthy',
                lastBackup: '2024-01-15T10:30:00Z'
            });
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
            setAlert({
                type: 'error',
                message: 'Failed to load dashboard data'
            });
        } finally {
            setLoading(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'users':
                return <UserManagement />;
            case 'admins':
                return <AdminManagement />;
            case 'upload':
                return <CertificateUpload onUploadComplete={loadDashboardData} />;
            case 'certificates':
                return <CertificateManagement />;
            case 'reports':
                return <VerificationReports />;
            case 'upload-reports':
                return <UploadReports />;
            case 'settings':
                return <SystemSettings />;
            case 'super-settings':
                return <SuperAdminSettings />;
            case 'analytics':
                return <SystemAnalytics analytics={analytics} systemHealth={systemHealth} />;
            case 'logs':
                return <ActivityLogs />;
            case 'overview':
            default:
                return (
                    <div>
                        <AdminOverview analytics={analytics} loading={loading} />
                        {systemHealth && (
                            <div className="mt-6">
                                <SystemHealthPanel systemHealth={systemHealth} />
                            </div>
                        )}
                    </div>
                );
        }
    };

    return (
        <div className="super-admin-dashboard">
            {alert && (
                <div className={`alert alert-${alert.type} mb-4`}>
                    {alert.message}
                    <button
                        onClick={() => setAlert(null)}
                        className="ml-4 text-lg font-bold cursor-pointer bg-transparent border-none"
                    >
                        ×
                    </button>
                </div>
            )}

            <div className="dashboard-header">
                <div>
                    <h1 className="dashboard-title">
                        <span className="text-red-600">🛡️</span> Super Admin Dashboard
                    </h1>
                    <p className="text-gray-600">
                        Complete system control and advanced administration tools.
                    </p>
                </div>

                <div className="user-info flex items-center gap-4">
                    <div className="text-right">
                        <div className="font-medium">{user.firstname} {user.lastname}</div>
                        <div className="text-sm text-red-600 font-semibold">Super Administrator</div>
                    </div>
                    <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.firstname.charAt(0)}{user.lastname.charAt(0)}
                    </div>
                </div>
            </div>

            {/* Enhanced Stats Cards - Only show on overview */}
            {activeTab === 'overview' && analytics && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                    <StatCard
                        title="Total Users"
                        value={analytics.total_users}
                        icon="👥"
                        color="blue"
                        change={`+${analytics.recent_users} this month`}
                        changeType="positive"
                    />
                    <StatCard
                        title="Total Certificates"
                        value={analytics.total_certificates}
                        icon="📜"
                        color="green"
                    />
                    <StatCard
                        title="Total Verifications"
                        value={analytics.total_authentications}
                        icon="✅"
                        color="purple"
                        change={`+${analytics.recent_authentications} this month`}
                        changeType="positive"
                    />
                    <StatCard
                        title="Total Revenue"
                        value={formatHelpers.formatCurrency(analytics.total_revenue)}
                        icon="💰"
                        color="yellow"
                        change={`+${formatHelpers.formatCurrency(analytics.recent_revenue)} this month`}
                        changeType="positive"
                    />
                    <StatCard
                        title="Success Rate"
                        value={`${analytics.success_rate}%`}
                        icon="📊"
                        color="green"
                    />
                </div>
            )}

            {/* Super Admin Quick Actions - Only show on overview */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
                    <QuickActionCard
                        icon="👤"
                        title="Manage Users"
                        description="View and manage all users"
                        onClick={() => setActiveTab('users')}
                        color="blue"
                    />
                    <QuickActionCard
                        icon="🛡️"
                        title="Manage Admins"
                        description="Admin role management"
                        onClick={() => setActiveTab('admins')}
                        color="red"
                    />
                    <QuickActionCard
                        icon="📤"
                        title="Upload Certificates"
                        description="Bulk certificate uploads"
                        onClick={() => setActiveTab('upload')}
                        color="green"
                    />
                    <QuickActionCard
                        icon="📊"
                        title="System Analytics"
                        description="Advanced system insights"
                        onClick={() => setActiveTab('analytics')}
                        color="purple"
                    />
                    <QuickActionCard
                        icon="⚙️"
                        title="Super Settings"
                        description="Advanced configurations"
                        onClick={() => setActiveTab('super-settings')}
                        color="yellow"
                    />
                    <QuickActionCard
                        icon="📋"
                        title="Activity Logs"
                        description="System audit trail"
                        onClick={() => setActiveTab('logs')}
                        color="gray"
                    />
                </div>
            )}

            {/* Tab Content */}
            <div className="tab-content">
                {renderTabContent()}
            </div>
        </div>
    );
};

// System Health Panel Component
const SystemHealthPanel = ({ systemHealth }) => {
    const getHealthColor = (status) => {
        switch (status) {
            case 'healthy': return 'text-green-600';
            case 'warning': return 'text-yellow-600';
            case 'error': return 'text-red-600';
            default: return 'text-gray-600';
        }
    };

    const getHealthIcon = (status) => {
        switch (status) {
            case 'healthy': return '✅';
            case 'warning': return '⚠️';
            case 'error': return '❌';
            default: return '❓';
        }
    };

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="text-lg font-semibold">System Health Status</h3>
            </div>
            <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                        <div className="text-2xl mb-2">
                            {getHealthIcon(systemHealth.database)}
                        </div>
                        <div className="font-medium">Database</div>
                        <div className={`text-sm ${getHealthColor(systemHealth.database)}`}>
                            {systemHealth.database}
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl mb-2">
                            {getHealthIcon(systemHealth.email)}
                        </div>
                        <div className="font-medium">Email Service</div>
                        <div className={`text-sm ${getHealthColor(systemHealth.email)}`}>
                            {systemHealth.email}
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl mb-2">
                            {getHealthIcon(systemHealth.storage)}
                        </div>
                        <div className="font-medium">File Storage</div>
                        <div className={`text-sm ${getHealthColor(systemHealth.storage)}`}>
                            {systemHealth.storage}
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl mb-2">💾</div>
                        <div className="font-medium">Last Backup</div>
                        <div className="text-sm text-gray-600">
                            {formatHelpers.formatDateTime(systemHealth.lastBackup)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Stat Card Component (Enhanced for Super Admin)
const StatCard = ({ title, value, icon, color, change, changeType }) => {
    const colorClasses = {
        blue: 'border-l-blue-500 bg-blue-50',
        green: 'border-l-green-500 bg-green-50',
        purple: 'border-l-purple-500 bg-purple-50',
        yellow: 'border-l-yellow-500 bg-yellow-50',
        red: 'border-l-red-500 bg-red-50'
    };

    const changeColorClasses = {
        positive: 'text-green-600',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
    };

    return (
        <div className={`stat-card ${colorClasses[color]} hover:shadow-lg transition-shadow`}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-2xl">{icon}</div>
                <div className="text-right">
                    <div className="stat-number">{value}</div>
                </div>
            </div>
            <div className="stat-label">{title}</div>
            {change && (
                <div className={`stat-change ${changeColorClasses[changeType]}`}>
                    {change}
                </div>
            )}
        </div>
    );
};

// Quick Action Card Component (Enhanced for Super Admin)
const QuickActionCard = ({ icon, title, description, onClick, color = 'blue' }) => {
    const colorClasses = {
        blue: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
        green: 'border-green-200 hover:border-green-400 hover:bg-green-50',
        purple: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50',
        yellow: 'border-yellow-200 hover:border-yellow-400 hover:bg-yellow-50',
        red: 'border-red-200 hover:border-red-400 hover:bg-red-50',
        gray: 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
    };

    const iconColorClasses = {
        blue: 'text-blue-600',
        green: 'text-green-600',
        purple: 'text-purple-600',
        yellow: 'text-yellow-600',
        red: 'text-red-600',
        gray: 'text-gray-600'
    };

    return (
        <div
            className={`card cursor-pointer transition-all duration-300 hover:shadow-lg ${colorClasses[color]}`}
            onClick={onClick}
        >
            <div className="card-body text-center">
                <div className={`text-2xl mb-2 ${iconColorClasses[color]}`}>
                    {icon}
                </div>
                <h3 className="text-sm font-semibold mb-1">{title}</h3>
                <p className="text-gray-600 text-xs">{description}</p>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;